import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cx } from 'linaria';
import {
  ModalContent,
  ModalFooter,
  ModalHeader,
  Portal,
  RegularButton,
  Typography,
  Divider,
} from 'fronton-react';
import { Modal } from 'fronton-react/modal';
import { InfoIcon } from '@fronton/icons-react';
import { analyticsUtils } from 'utils';
import { Skeleton } from 'common/components/skeleton';
import { GET_PRODUCT_MERCHANT_INFO } from 'features/product-info/ducks/product-merchant-info';
import { getMerchantInfo, getMerchantError } from 'features/product-info/ducks/selectors';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { getIsEcommerce } from 'features/goods-supply';
import { showNotification } from '@navi-toolbox/ui-components';
import { NotificationTemplates } from 'common/components/notification-template-factory/types';
import { getTrustMerchant } from 'features/product-quality-documents/ducks/selector';
import { FETCH_TRUST_DOCUMENTS } from 'features/product-quality-documents/ducks/actions';
import { getEnableCertificates } from 'features/page-model';
import {
  container,
  labelWrapper,
  iconButtonStyle,
  divider,
  skeleton,
  skeletonShort,
  merchantInfoHeading,
  modalContent,
  onlineOrderOnlyMessageTextStyle,
} from './styles';

export const SKELETON_ROWS = 4;
const DATA_QA_TIME_IN_STORE_CAPTION = 'online_order_only_message_in_store_caption';
const DATA_QA_HAS_CERTIFICATE_LABEL = 'has-certificate-label';

export const OnlineOrderOnlyMessage = () => {
  const dispatch = useDispatch();
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);

  const merchantInfo = useSelector(getMerchantInfo);
  const trustMerchantInfo = useSelector(getTrustMerchant);
  const enableCertificates = useSelector(getEnableCertificates);
  const isEcommerce = useSelector(getIsEcommerce);
  const error = useSelector(getMerchantError);
  const regionId = useSelector(getRegionId) || '';
  const { t } = useTranslation();

  const merchant = enableCertificates ? trustMerchantInfo : merchantInfo;

  const canSellMarkingProducts = enableCertificates
    ? !!trustMerchantInfo?.isAllowedToSellMarkingProducts
    : false;

  const openModal = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'productDetail',
      elementName: 'onlineOrder3P',
      elementText: t('product-info.online-order-only.title'),
    });
    setModalVisibility(true);

    if (merchant) return;

    dispatch(enableCertificates ? FETCH_TRUST_DOCUMENTS.request() : GET_PRODUCT_MERCHANT_INFO({ regionId }));
  };

  useEffect(() => {
    if (error) {
      showNotification({
        templateId: NotificationTemplates.OnlineOrderError,
      });
    }
  }, [error]);

  const renderSkeleton = () =>
    Array(SKELETON_ROWS)
      .fill('')
      .map((_, index) => (
        <Skeleton
          key={index}
          className={cx(skeleton, index % 2 ? null : skeletonShort)}
          blink={true}
          height={'10px'}
        />
      ));

  return (
    <div className={container} data-qa="online-order-only-message">
      <div className={labelWrapper}>
        <Typography
          variant="s"
          size="body_accent"
          color="secondary-blue-primary"
          className={onlineOrderOnlyMessageTextStyle}
          data-qa="online-order-only-message-text"
        >
          {t('product-info.online-order-only.title')}
        </Typography>
        {!isEcommerce && (
          <InfoIcon
            color="control-primary"
            onClick={openModal}
            data-qa="info-icon"
            size="l"
            className={iconButtonStyle}
          />
        )}
      </div>
      <Typography variant="caption" color="text-primary">
        {t('product-info.online-order-only.part1')}
      </Typography>
      <Portal>
        <Modal
          show={modalVisibility}
          modalID="merchant-info"
          onClose={() => setModalVisibility(false)}
          size="s"
          variant="curtain"
        >
          <ModalHeader title={t('product-info.online-order-only.title')} />
          <ModalContent className={modalContent}>
            <Typography variant="m" size="body_long">
              {t('product-info.online-order-only.part2')}
            </Typography>
            <Divider className={divider} />
            <Typography
              as="p"
              variant="minor"
              size="overline"
              color="text-secondary"
              className={merchantInfoHeading}
            >
              {t('product-info.online-order-only.part3')}
            </Typography>
            {merchant ? (
              <>
                {merchant.name && (
                  <Typography as="p" variant="caption" color="text-secondary">
                    {merchant.name}
                  </Typography>
                )}
                {merchant.address && (
                  <Typography as="p" variant="caption" color="text-secondary">
                    {merchant.address}
                  </Typography>
                )}
                {merchant.legalRegistrationNumber && (
                  <Typography as="p" variant="caption" color="text-secondary">
                    {merchant.legalRegistrationNumber}
                  </Typography>
                )}
                <Typography
                  as="p"
                  variant="caption"
                  color="text-secondary"
                  data-qa={DATA_QA_TIME_IN_STORE_CAPTION}
                >
                  {t('product-info.online-order-only.part4', {
                    brand: 'Лемана ПРО',
                  })}
                </Typography>
                {canSellMarkingProducts && (
                  <Typography
                    as="p"
                    variant="caption"
                    color="text-secondary"
                    data-qa={DATA_QA_HAS_CERTIFICATE_LABEL}
                  >
                    {t('product-info.manuals.seller-is-registered')}
                  </Typography>
                )}
              </>
            ) : (
              renderSkeleton()
            )}
          </ModalContent>
          <ModalFooter>
            <RegularButton
              aria-label={t('product-info.online-order-only.got-it')}
              onClick={() => setModalVisibility(false)}
              size="l"
              variant="secondary"
              wide
            >
              {t('product-info.online-order-only.got-it')}
            </RegularButton>
          </ModalFooter>
        </Modal>
      </Portal>
    </div>
  );
};
